import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  List,
  ListItem,
  Tabs,
  Tab,
  Paper,
  IconButton,
  Chip,
  LinearProgress,
  alpha,
  Divider,
  Tooltip,
  CircularProgress,
  Collapse,
  Stack,
} from '@material-ui/core'
import React, { useEffect, useState, useRef, useMemo } from 'react'
import axios from '../../utils/axios'
import { createGeneSet } from '../../api/gene/geneSet'
import { useImmer } from 'use-immer'
import UploadMultiPairFile from '../../components/upload/UploadMultiPairFile'
import { UploadMultiFile } from '../../components/upload'
import { useFiles, useBedFiles } from '../../api/file'
import { chemistryField, chemistrySupported, DEFAULT_CHEMISTRY } from 'src/utils/bedChemistry'
import BedChemistryField from '../KitPanelUploadView/BedChemistryField'
import { useSnackbar } from 'notistack5'
import validGenes from '../../constants/genes.json'
import InfoIcon from '@material-ui/icons/Info'
import CloudUploadIcon from '@material-ui/icons/CloudUpload'
import DeleteIcon from '@material-ui/icons/Delete'
import CancelIcon from '@material-ui/icons/Cancel'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import DescriptionIcon from '@material-ui/icons/Description'
import TimerIcon from '@material-ui/icons/Timer'
import styled from '@emotion/styled'
import ClickAwayTooltip from 'src/components/ClickAwayTooltip'
import { API_BASE_URL } from '../../constants'
import * as tus from 'tus-js-client'
import { useUploadTrackerStore } from '../../zustand/uploadStore'
import uploadService from '../../services/UploadService'
import { motion } from 'framer-motion'
import { useDropzone } from 'react-dropzone'
import { fData } from '../../utils/formatNumber'
import { useTheme } from '@material-ui/core/styles'
import { toFileKey, detectRead, shortenFileName } from '../../utils/fastqNaming'
import FastqNamingInfo from './components/FastqNamingInfo'
import FastqNamingCheck from './components/FastqNamingCheck'
import ResultFileDialog from './components/ResultFileDialog'
import NewAnalysisDialog from '../NewAnalysis/NewAnalysisDialog'
import { isResultFile } from '../../utils/resultFiles'

// File type validation
const FILE_TYPES = {
  FASTQ: 'FASTQ',
  VCF: 'VCF',
  BED: 'BED',
  BAM: 'BAM',
  GENE_PANEL: 'GENE_PANEL',
  INVALID: 'INVALID',
}

const getFileType = (filename) => {
  if (/\.(fastq|fq)(\.gz)?$/i.test(filename)) {
    return FILE_TYPES.FASTQ
  }
  if (/\.vcf(\.gz)?$/i.test(filename)) {
    return FILE_TYPES.VCF
  }
  if (/\.bam$/i.test(filename)) {
    return FILE_TYPES.BAM
  }
  if (/\.bed$/i.test(filename)) {
    return FILE_TYPES.BED
  }
  if (/\.(txt|csv)$/i.test(filename)) {
    return FILE_TYPES.GENE_PANEL
  }
  return FILE_TYPES.INVALID
}

// Group and order the queue exactly as the server will pair the uploads, by
// delegating to the ported to_file_key() / read detection in utils/fastqNaming.
//
// The previous local implementations were a hand-listed chain of str.replace()
// calls that had drifted from the backend: dotted forms such as
// "sample.1.fastq.gz" reduced to "sample.1" here but to "sample" on the server,
// so the preview grouped files differently from the way they actually paired.
const toFileId = (file) => toFileKey(file.name)

const isRightRead = (filename) => detectRead(filename) === 2

// The New analysis panel's workflow for an analysis started from a result file.
const SOMATIC_VCF_WORKFLOW = { analysis: 'somatic', input: 'vcf' }

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 16,
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.05)',
  overflow: 'hidden',
}))

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(1.5, 2, 2, 2),
  position: 'relative',
  minHeight: 0,
}))

// Header styles aligned with Analysis Distribution card header
const UploadHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(1.5),
  marginTop: theme.spacing(0.5),
  padding: theme.spacing(0.5, 1, 1, 1),
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
}))

const UploadTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  letterSpacing: '0.3px',
}))

// (No extra container; keep body styling unchanged)

const FileListContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  maxHeight: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
}))

const BrowseFilesButton = styled(Button)(({ theme }) => ({
  borderRadius: '12px',
  textTransform: 'none',
  padding: theme.spacing(1, 2),
  fontSize: '0.875rem',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  flexShrink: 0,
  border: `2px dashed ${alpha(theme.palette.primary.main, 0.5)}`,
  backgroundColor: alpha(theme.palette.primary.main, 0.05),
  color: theme.palette.primary.main,
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    borderColor: theme.palette.primary.main,
    transform: 'translateY(-1px)',
    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
  },
}))

const ScrollableFileContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  paddingRight: theme.spacing(1),
  paddingBottom: theme.spacing(16),
  maxHeight: '400px',
  minHeight: '200px',
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: alpha(theme.palette.divider, 0.1),
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: alpha(theme.palette.primary.main, 0.4),
    borderRadius: '4px',
    '&:hover': {
      background: alpha(theme.palette.primary.main, 0.6),
    },
  },
}))

const FileCountBadge = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  padding: theme.spacing(0.5, 1),
  borderRadius: '12px',
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  color: theme.palette.primary.main,
  fontSize: '0.75rem',
  fontWeight: 600,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
}))

const FileTypeLink = styled('span')({
  textDecoration: 'underline',
  cursor: 'pointer',
  transition: 'color 0.2s ease',
  '&:hover': {
    color: 'primary.main',
  },
})

const StyledTabs = styled(Tabs)(({ theme }) => ({
  marginBottom: 16,
  minHeight: 42,
  flexShrink: 0,
  '& .MuiTabs-indicator': {
    height: 3,
    borderRadius: '3px 3px 0 0',
  },
  '& .MuiTab-root': {
    minHeight: 42,
    textTransform: 'none',
    fontWeight: 500,
    transition: 'all 0.2s ease',
    marginRight: theme.spacing(3),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    color: theme.palette.text.secondary,
    '&.Mui-selected': {
      color: theme.palette.primary.main,
    },
  },
}))

const DropZoneStyle = styled('div')(({ theme, hasFiles }) => ({
  outline: 'none',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: hasFiles ? theme.spacing(2, 2) : theme.spacing(6, 2),
  minHeight: hasFiles ? '140px' : '380px',
  height: hasFiles ? '140px' : 'auto',
  borderRadius: 16,
  backgroundColor: alpha(theme.palette.primary.lighter, 0.1),
  border: `2px dashed ${alpha(theme.palette.primary.main, 0.5)}`,
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.lighter, 0.15),
    cursor: 'pointer',
  },
}))

const FileItemStyle = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5),
  marginBottom: theme.spacing(1),
  borderRadius: 12,
  border: `1px solid ${theme.palette.divider}`,
  transition: 'all 0.25s ease',
  overflow: 'hidden',
  backgroundColor: alpha(theme.palette.background.paper, 0.5),
  backdropFilter: 'blur(8px)',
  '&:hover': {
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
    transform: 'translateY(-2px)',
    borderColor: alpha(theme.palette.primary.main, 0.25),
  },
}))

const FileTypeChip = styled(Chip)(({ theme }) => ({
  borderRadius: 4,
  fontSize: '0.75rem',
  height: 24,
  fontWeight: 500,
  textTransform: 'uppercase',
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  color: theme.palette.primary.main,
  border: 'none',
  '&.MuiChip-filledSuccess': {
    backgroundColor: alpha(theme.palette.success.main, 0.1),
    color: theme.palette.success.dark,
  },
  '&.MuiChip-filledError': {
    backgroundColor: alpha(theme.palette.error.main, 0.1),
    color: theme.palette.error.dark,
  },
  '&.MuiChip-filledWarning': {
    backgroundColor: alpha(theme.palette.warning.main, 0.1),
    color: theme.palette.warning.dark,
  },
  '&.MuiChip-filledInfo': {
    backgroundColor: alpha(theme.palette.info.main, 0.1),
    color: theme.palette.info.dark,
  },
}))

const UploadButton = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  textTransform: 'none',
  boxShadow: 'none',
  padding: theme.spacing(0.75, 2.5),
  fontSize: '0.85rem',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  flexShrink: 0,
  transition: 'all 0.2s ease',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    transform: 'translateY(-1px)',
  },
  '&.main-upload-button': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(1, 3),
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
}))

const FileDetailGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr 1fr',
  gap: theme.spacing(1.5),
  width: '100%',
  marginTop: theme.spacing(0.5),
  padding: theme.spacing(1),
  backgroundColor: alpha(theme.palette.background.default, 0.4),
  borderRadius: 8,
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr 1fr',
  },
}))

const DetailItem = styled(Box)(({ theme }) => ({
  '.MuiTypography-caption': {
    display: 'block',
    marginBottom: theme.spacing(0.5),
    textAlign: 'center',
  },
  '.MuiTypography-body2': {
    textAlign: 'center',
    display: 'flex',
    justifyContent: 'center',
  },
}))

const ProgressBarStyle = styled(LinearProgress)(({ theme }) => ({
  height: 6,
  borderRadius: 3,
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
}))

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(3),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(2, 3),
  },
  '& .MuiDialog-paper': {
    borderRadius: 16,
    boxShadow: theme.shadows[5],
  },
}))

// TabPanel component for organizing content
const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`upload-tabpanel-${index}`}
      aria-labelledby={`upload-tab-${index}`}
      style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      {...other}
    >
      {value === index && <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>{children}</Box>}
    </div>
  )
}

// Function to get tab props for accessibility
const a11yProps = (index) => {
  return {
    id: `upload-tab-${index}`,
    'aria-controls': `upload-tabpanel-${index}`,
  }
}

// Largest file the dropzone accepts; prod OBS multipart (1GB parts x 10000) allows far more.
const MAX_UPLOAD_SIZE_BYTES = 1024 * 1024 * 1024 * 500 // 500GB

// Format file size utility
const formatFileSize = (bytes) => {
  return fData(bytes)
}

// Format time utility
const formatTimeRemaining = (seconds) => {
  if (!seconds || seconds <= 0) return 'Calculating...'
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  return `${minutes}m ${seconds % 60}s`
}

// Add a styled component for paired files
const PairedFileContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginTop: theme.spacing(1),
  padding: theme.spacing(1.5),
  backgroundColor: alpha(theme.palette.background.default, 0.4),
  borderRadius: 8,
}))

const UnifiedFileUpload = () => {
  const theme = useTheme()
  const [fastqQueue, setFastqQueue] = useImmer(new Map())
  const [vcfQueue, setVcfQueue] = useState([])
  const [bamQueue, setBamQueue] = useState([])
  const [bedQueue, setBedQueue] = useState([])
  const [genePanelQueue, setGenePanelQueue] = useState([])
  const [state, setState] = useState('IDLE')
  const [uploadProgress, setUploadProgress] = useState({ percentCompleted: 0, timeRemaining: 0 })
  const [uploadedFiles, setUploadedFiles] = useState([])
  const [invalidGenes, setInvalidGenes] = useState([])
  const [isGeneValidationDialogOpen, setIsGeneValidationDialogOpen] = useState(false)
  const [isNotesDialogOpen, setIsNotesDialogOpen] = useState(false)
  const [notes, setNotes] = useState('')
  const [activeTab, setActiveTab] = useState(0)
  // Result files from a tumour test done elsewhere: their own upload, opened from
  // its entry below the drop zone or by the files dropped here (utils/resultFiles).
  const [resultUpload, setResultUpload] = useState({ open: false, files: [] })
  // A somatic analysis started from a result file: the New analysis panel on the
  // upload the server registered from it.
  const [resultStart, setResultStart] = useState({ open: false, files: [] })
  const filesApi = useFiles()
  const { query: bedFilesQuery, refresh: refreshBedFiles } = useBedFiles()
  // The chemistry choice is offered (and sent) only once the kit list carries one.
  const bedChemistryOn = chemistrySupported(bedFilesQuery.data)
  const { enqueueSnackbar } = useSnackbar()

  // BAM platform selector state
  const [bamPlatformDialogOpen, setBamPlatformDialogOpen] = useState(false)
  const [pendingBamFiles, setPendingBamFiles] = useState([])
  const [bamPlatformMap, setBamPlatformMap] = useState({}) // filename -> 'ont_bam' | 'pacbio_bam'

  // VCF variant-type selector state. Each pending file gets its own choice
  // (SNP/Indel | CNV | SV); one Confirm applies all selections.
  const [vcfVariantTypeDialogOpen, setVcfVariantTypeDialogOpen] = useState(false)
  const [pendingVcfFiles, setPendingVcfFiles] = useState([])
  const [pendingVcfTypes, setPendingVcfTypes] = useState({}) // filename -> 'snp_indel' | 'cnv' | 'sv'
  const [vcfVariantTypeMap, setVcfVariantTypeMap] = useState({}) // filename -> 'snp_indel' | 'cnv' | 'sv'

  const handleVcfVariantTypeConfirm = () => {
    const newMap = { ...vcfVariantTypeMap }
    pendingVcfFiles.forEach((file) => {
      newMap[file.name] = pendingVcfTypes[file.name] || 'snp_indel'
    })
    setVcfVariantTypeMap(newMap)
    setVcfQueue((prev) => [...prev, ...pendingVcfFiles])
    setPendingVcfFiles([])
    setPendingVcfTypes({})
    setVcfVariantTypeDialogOpen(false)
    setState('READY_TO_UPLOAD')
  }

  const handleVcfVariantTypeCancel = () => {
    setPendingVcfFiles([])
    setPendingVcfTypes({})
    setVcfVariantTypeDialogOpen(false)
  }

  const handlePendingVcfTypeChange = (filename, value) => {
    setPendingVcfTypes((prev) => ({ ...prev, [filename]: value }))
  }

  const [bedDialogOpenFor, setBedDialogOpenFor] = useState(null)
  const handleCloseBedDialog = () => {
    setBedDialogOpenFor(null)
  }

  const [selectedBamPlatform, setSelectedBamPlatform] = useState('ont_bam')

  const handleBamPlatformConfirm = () => {
    // Map all pending BAM files to the selected platform
    const newMap = { ...bamPlatformMap }
    pendingBamFiles.forEach((file) => {
      newMap[file.name] = selectedBamPlatform
    })
    setBamPlatformMap(newMap)
    setBamQueue((prev) => [...prev, ...pendingBamFiles])
    setPendingBamFiles([])
    setBamPlatformDialogOpen(false)
    setSelectedBamPlatform('ont_bam')
    setState('READY_TO_UPLOAD')
  }

  const handleBamPlatformCancel = () => {
    setPendingBamFiles([])
    setBamPlatformDialogOpen(false)
    setSelectedBamPlatform('ont_bam')
  }

  const [bedDialogOpenForAll, setBedDialogOpenForAll] = useState(false)
  const handleCloseBedDialogForAll = () => {
    setBedDialogOpenForAll(false)
  }

  // Store active upload IDs for cancellation
  const [activeUploadIds, setActiveUploadIds] = useState({})
  const [bedFileStates, setBedFileStates] = useState(new Map()) // Map to track bed file states: 'processing', 'unverified', 'verified'
  const [bedReferences, setBedReferences] = useState({})
  const [bedChemistries, setBedChemistries] = useState({})

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
  }

  // Check for interrupted uploads when component mounts
  useEffect(() => {
    // Let the upload service handle interrupted uploads automatically
    uploadService.init()
  }, [])

  const getFilesToUpload = () => {
    const fastqFiles = []
    fastqQueue.forEach((entry) => {
      ;[entry.left, entry.right].forEach((file) => {
        if (file && !uploadedFiles.includes(file)) {
          fastqFiles.push(file)
        }
      })
    })
    const vcfFiles = vcfQueue.filter((f) => !uploadedFiles.includes(f))
    const bamFiles = bamQueue.filter((f) => !uploadedFiles.includes(f))
    const bedFiles = bedQueue.filter((f) => !uploadedFiles.includes(f))
    const genePanelFiles = genePanelQueue.filter((f) => !uploadedFiles.includes(f))
    return [...fastqFiles, ...vcfFiles, ...bamFiles, ...bedFiles, ...genePanelFiles]
  }

  const filesToUpload = getFilesToUpload()

  const readFileContent = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (event) => resolve(event.target.result)
      reader.onerror = (error) => reject(error)
      reader.readAsText(file)
    })
  }

  const validateGenePanel = async (file) => {
    const content = await readFileContent(file)
    const genes = content
      .split(/[\n,]/)
      .map((gene) => gene.trim())
      .filter((gene) => gene)
    const invalid = genes.filter((gene) => !validGenes.includes(gene))

    if (invalid.length > 0) {
      setInvalidGenes(invalid)
      setIsGeneValidationDialogOpen(true)
      return false
    }
    return true
  }

  const handleDropFile = async (allDroppedFiles) => {
    const isResult = await Promise.all(allDroppedFiles.map((file) => isResultFile(file)))
    const resultFiles = allDroppedFiles.filter((_file, i) => isResult[i])
    const droppedFiles = allDroppedFiles.filter((_file, i) => !isResult[i])
    if (resultFiles.length > 0) {
      setResultUpload({ open: true, files: resultFiles })
      if (droppedFiles.length === 0) return
    }

    const fastqFiles = []
    const vcfFiles = []
    const bamFiles = []
    const bedFiles = []
    const genePanelFiles = []
    const invalidFiles = []

    droppedFiles.forEach((file) => {
      const fileType = getFileType(file.name)
      switch (fileType) {
        case FILE_TYPES.FASTQ:
          fastqFiles.push(file)
          break
        case FILE_TYPES.VCF:
          vcfFiles.push(file)
          break
        case FILE_TYPES.BAM:
          bamFiles.push(file)
          break
        case FILE_TYPES.BED:
          bedFiles.push(file)
          break
        case FILE_TYPES.GENE_PANEL:
          genePanelFiles.push(file)
          break
        default:
          invalidFiles.push(file)
      }
    })

    if (invalidFiles.length > 0) {
      enqueueSnackbar(`Invalid file type(s): ${invalidFiles.map((f) => f.name).join(', ')}`, {
        variant: 'error',
      })
      return
    }

    // Handle FASTQ files
    fastqFiles.forEach((file) => {
      const fileId = toFileId(file)
      const isRight = isRightRead(file.name)

      setFastqQueue((draft) => {
        if (!draft.has(fileId)) {
          draft.set(fileId, { left: null, right: null })
        }

        const entry = draft.get(fileId)
        if (isRight || entry.left) {
          entry.right = file
        } else {
          entry.left = file
        }
      })
    })

    // If VCF files were dropped, show variant-type selector dialog
    // (files are queued only after user picks SNP/Indel | CNV | SV per file).
    if (vcfFiles.length > 0) {
      setPendingVcfFiles(vcfFiles)
      setPendingVcfTypes((prev) => {
        const next = { ...prev }
        vcfFiles.forEach((file) => {
          if (!next[file.name]) next[file.name] = 'snp_indel'
        })
        return next
      })
      setVcfVariantTypeDialogOpen(true)
    }
    // If BAM files were dropped, show platform selector dialog
    if (bamFiles.length > 0) {
      setPendingBamFiles(bamFiles)
      setBamPlatformDialogOpen(true)
    }
    setBedQueue((prev) => [...prev, ...bedFiles])

    if (bedFiles.length > 0) {
      setBedReferences((refs) => {
        const newRefs = { ...refs }
        bedFiles.forEach((file) => {
          if (!(file.name in newRefs)) {
            newRefs[file.name] = undefined
          }
        })
        return newRefs
      })
    }
    setGenePanelQueue((prev) => [...prev, ...genePanelFiles])

    if (genePanelFiles.length > 0) {
      const isValid = await validateGenePanel(genePanelFiles[0])
      if (isValid) {
        setIsNotesDialogOpen(true)
      }
    } else if (fastqFiles.length > 0 || bedFiles.length > 0) {
      setState('READY_TO_UPLOAD')
    }
    // VCF and BAM files go through their own selector dialogs; state is
    // advanced after the user confirms the variant type / platform.
  }

  const handleUploadFiles = async () => {
    setState('UPLOADING')
  }

  const handleRemoveAll = () => {
    setFastqQueue(new Map())
    setVcfQueue([])
    setBamQueue([])
    setBedQueue([])
    setGenePanelQueue([])
    setUploadProgress({ percentCompleted: 0, timeRemaining: 0 })
    setUploadedFiles([])
    setBedReferences({})
    setBedChemistries({})
    setVcfVariantTypeMap({})
  }

  const handleRemove = (file) => {
    const fileType = getFileType(file.name)

    if (fileType === FILE_TYPES.FASTQ) {
      setFastqQueue((draft) => {
        const fileId = toFileId(file)
        const entry = draft.get(fileId)

        if (entry.left === file) {
          entry.left = null
        } else if (entry.right === file) {
          entry.right = null
        }

        if (!entry.left && !entry.right) {
          draft.delete(fileId)
        }
      })
    } else if (fileType === FILE_TYPES.VCF) {
      setVcfQueue((prev) => prev.filter((f) => f !== file))
      setVcfVariantTypeMap((prev) => {
        if (!(file.name in prev)) return prev
        const next = { ...prev }
        delete next[file.name]
        return next
      })
    } else if (fileType === FILE_TYPES.BAM) {
      setBamQueue((prev) => prev.filter((f) => f !== file))
    } else if (fileType === FILE_TYPES.GENE_PANEL) {
      setGenePanelQueue((prev) => prev.filter((f) => f !== file))
    } else {
      setBedQueue((prev) => prev.filter((f) => f !== file))
    }

    setBedReferences((refs) => ({ ...refs, [file.name]: undefined }))
  }

  const handleCancel = () => {
    // Cancel all active uploads through the service
    Object.values(activeUploadIds).forEach((id) => {
      uploadService.cancelUpload(id)
    })

    setActiveUploadIds({})
    handleRemoveAll()
    setState('IDLE')
  }

  const uploadGenePanel = async (file) => {
    const content = await readFileContent(file)
    const genes = content
      .split(/[\n,]/)
      .map((gene) => gene.trim())
      .filter((gene) => gene)
    await createGeneSet(file.name.replace(/\.[^.]+$/, ''), genes, notes)
    setNotes('')
    enqueueSnackbar('Gene panel uploaded successfully', { variant: 'success' })
  }

  const uploadFile = async (file) => {
    const fileType = getFileType(file.name)
    const metadata = {}

    // Add metadata for BED files
    if (fileType === FILE_TYPES.BED) {
      metadata.reference = bedReferences[file.name]
      Object.assign(metadata, chemistryField(bedChemistries[file.name] || DEFAULT_CHEMISTRY, bedChemistryOn))
    }

    // Tell the backend which kind of VCF this is (SNP/Indel | CNV | SV).
    // Collected from the variant-type dialog that opens when VCFs are dropped.
    if (fileType === FILE_TYPES.VCF) {
      metadata.variant_type = vcfVariantTypeMap[file.name] || 'snp_indel'
    }

    // For BAM files, use the platform-specific category
    const fileCategory =
      fileType === FILE_TYPES.BAM && bamPlatformMap[file.name] ? bamPlatformMap[file.name] : undefined

    // Start the upload using our service
    const uploadId = await uploadService.startUpload(file, {
      metadata,
      fileCategory,
      onProgress: (percentage, timeRemaining) => {
        setUploadProgress({
          percentCompleted: percentage,
          timeRemaining: timeRemaining,
        })
      },
      onSuccess: () => {
        // Note: setUploadedFiles is handled by the state machine after uploadFile returns
        // to avoid duplicate entries and race conditions with fast uploads
        filesApi.refresh()
        refreshBedFiles()

        // Add BED files to tracking after upload is finished
        if (fileType === FILE_TYPES.BED) {
          // Initially set as processing
          setBedFileStates((prev) => new Map(prev).set(file.name, 'processing'))

          // Start checking verification status periodically
          const checkVerification = setInterval(async () => {
            try {
              const bedFilesResponse = await axios.get(`${API_BASE_URL}/bed/list`)
              const uploadedBedFile = bedFilesResponse.data.find(
                (bf) => bf.visible_name === file.name || bf.name === file.name,
              )

              if (uploadedBedFile) {
                const isProcessed = uploadedBedFile.is_processed !== false
                const isVerified = uploadedBedFile.is_verified !== false

                if (!isProcessed) {
                  // Still processing
                  setBedFileStates((prev) => new Map(prev).set(file.name, 'processing'))
                } else if (!isVerified) {
                  // Processed but not verified
                  setBedFileStates((prev) => new Map(prev).set(file.name, 'unverified'))
                } else {
                  // Processed and verified - remove from tracking
                  setBedFileStates((prev) => {
                    const newMap = new Map(prev)
                    newMap.delete(file.name)
                    return newMap
                  })
                  clearInterval(checkVerification)
                }
              }
            } catch (error) {
              console.error('Error checking bed file verification:', error)
            }
          }, 3000)

          // Stop checking after 5 minutes
          setTimeout(() => {
            clearInterval(checkVerification)
            setBedFileStates((prev) => {
              const newMap = new Map(prev)
              newMap.delete(file.name)
              return newMap
            })
          }, 300000)
        }
      },
      onError: (error) => {
        enqueueSnackbar(`Failed to upload ${file.name}`, { variant: 'error' })
      },
    })

    if (!uploadId) {
      enqueueSnackbar(`Failed to start upload for ${file.name}`, { variant: 'error' })
      return null
    }

    // Store the upload ID for potential cancellation
    setActiveUploadIds((prev) => ({ ...prev, [file.name]: uploadId }))

    return uploadId
  }

  useEffect(() => {
    const uploadFilesStateMachine = async () => {
      if (state === 'UPLOADING') {
        const allFiles = getFilesToUpload()

        if (allFiles.length > 0) {
          const nextFile = allFiles[0]
          const fileType = getFileType(nextFile.name)

          try {
            if (fileType === FILE_TYPES.GENE_PANEL) {
              await uploadGenePanel(nextFile)
            } else {
              await uploadFile(nextFile)
            }

            setUploadProgress({ percentCompleted: 0, timeRemaining: 0 })
            setUploadedFiles((prev) => [...prev, nextFile])
            await filesApi.refresh()
          } catch (error) {
            enqueueSnackbar(`Failed to upload ${nextFile.name}`, { variant: 'error' })
          }
        } else {
          setState('IDLE')
          handleRemoveAll()
        }
      }
    }

    uploadFilesStateMachine()
  }, [state, fastqQueue, vcfQueue, bamQueue, bedQueue, genePanelQueue, uploadedFiles])

  // Updated renderDropzone function that doesn't call useDropzone
  const renderDropzone = () => {
    const hasFiles = hasAnyFiles()
    return (
      <DropZoneStyle
        {...getRootProps()}
        hasFiles={hasFiles}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          ...(isDragActive && {
            borderColor: 'primary.main',
            backgroundColor: (theme) => alpha(theme.palette.primary.lighter, 0.3),
          }),
          ...(state === 'UPLOADING' && {
            opacity: 0.6,
            pointerEvents: 'none',
          }),
        }}
      >
        <input {...getInputProps()} disabled={state === 'UPLOADING'} />

        <CloudUploadIcon
          sx={{
            fontSize: 64,
            color: 'primary.main',
            mb: 2,
            opacity: 0.8,
          }}
        />

        <Typography variant="h6" sx={{ mb: 1 }}>
          {isDragActive ? 'Drop files here' : 'Drag & drop files here'}
        </Typography>

        <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', mb: 3 }}>
          or
          <Button variant="outlined" component="span" sx={{ mx: 1 }} disabled={state === 'UPLOADING'}>
            Browse Files
          </Button>
          from your computer
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            justifyContent: 'center',
            maxWidth: 500,
          }}
        >
          {[
            { label: 'FASTQ', exts: ['.fastq', '.fq', '.fastq.gz', '.fq.gz'] },
            { label: 'VCF', exts: ['.vcf', '.vcf.gz'] },
            { label: 'BAM', exts: ['.bam'] },
            { label: 'BED', exts: ['.bed'] },
            { label: 'Gene Panels', exts: ['.txt', '.csv'] },
          ].map((item) => (
            <Chip
              key={item.label}
              label={item.label}
              size="small"
              variant="outlined"
              sx={{
                borderRadius: 1,
                backgroundColor: 'background.paper',
                '& .MuiChip-label': {
                  px: 1,
                },
              }}
            />
          ))}
        </Box>
      </DropZoneStyle>
    )
  }

  // Render file item for the list
  const renderFileItem = (file, index, fileType) => {
    const isUploading = state === 'UPLOADING' && !uploadedFiles.includes(file) && filesToUpload[0] === file
    const isUploaded = uploadedFiles.includes(file)
    const fileStatus = getFileStatus(file)
    const dateModified = new Date(file.lastModified).toLocaleString()
    const isBedFile = fileType === FILE_TYPES.BED
    const bedReference = isBedFile ? bedReferences[file.name] : undefined

    // Get a color based on file type
    const getTypeColor = (type) => {
      switch (type) {
        case FILE_TYPES.FASTQ:
          return 'primary'
        case FILE_TYPES.VCF:
          return 'info'
        case FILE_TYPES.BAM:
          return 'secondary'
        case FILE_TYPES.BED:
          return 'warning'
        case FILE_TYPES.GENE_PANEL:
          return 'success'
        default:
          return 'default'
      }
    }

    // Get appropriate file icon based on file type
    const getFileIcon = (type) => {
      switch (type) {
        case FILE_TYPES.FASTQ:
          return <DescriptionIcon sx={{ color: 'primary.main' }} />
        case FILE_TYPES.VCF:
          return <DescriptionIcon sx={{ color: 'info.main' }} />
        case FILE_TYPES.BAM:
          return <DescriptionIcon sx={{ color: 'secondary.main' }} />
        case FILE_TYPES.BED:
          return <DescriptionIcon sx={{ color: 'warning.main' }} />
        case FILE_TYPES.GENE_PANEL:
          return <DescriptionIcon sx={{ color: 'success.main' }} />
        default:
          return <DescriptionIcon />
      }
    }

    // Handle individual file upload
    const handleUploadSingleFile = async () => {
      try {
        if (fileType === FILE_TYPES.GENE_PANEL) {
          await uploadGenePanel(file)
        } else {
          await uploadFile(file)
        }
        setUploadedFiles((prev) => [...prev, file])
        filesApi.refresh()
        refreshBedFiles()
      } catch (error) {
        enqueueSnackbar(`Failed to upload ${file.name}`, { variant: 'error' })
      }
    }

    return (
      <motion.div
        key={`${file.name}-${index}`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, x: -10 }}
        transition={{ duration: 0.2, delay: index * 0.05 }}
      >
        <FileItemStyle>
          {/* Main row with file info and actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <Box
                sx={{
                  mr: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 40,
                  height: 40,
                  borderRadius: 1,
                  bgcolor: (theme) => alpha(theme.palette.background.default, 0.7),
                }}
              >
                {getFileIcon(fileType)}
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <Typography
                    variant="subtitle2"
                    noWrap
                    title={file.name}
                    sx={{ maxWidth: { xs: 150, sm: 250, md: 350 } }}
                  >
                    {shortenFileName(file.name, 36)}
                  </Typography>
                  <FileTypeChip label={fileType} size="small" color={getTypeColor(fileType)} sx={{ ml: 1 }} />
                  {isBedFile && bedFileStates.has(file.name) && (
                    <Chip
                      label={bedFileStates.get(file.name) === 'processing' ? 'Verifying...' : 'Unverified'}
                      size="small"
                      sx={{
                        ml: 1,
                        backgroundColor: 'warning.main',
                        color: 'warning.contrastText',
                        fontWeight: 'bold',
                      }}
                    />
                  )}
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  component="div"
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  {formatFileSize(file.size)}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {!isUploaded && !isUploading && (
                <Tooltip title={isBedFile && !bedReference ? 'Select reference genome' : 'Upload this file'}>
                  <span>
                    <UploadButton
                      variant="contained"
                      size="small"
                      startIcon={<CloudUploadIcon fontSize="small" />}
                      onClick={() => {
                        if (isBedFile) {
                          setBedDialogOpenFor(file.name)
                        } else {
                          handleUploadSingleFile()
                        }
                      }}
                      disabled={state === 'UPLOADING' || (isBedFile && !bedReference)}
                      color="primary"
                      sx={{ minWidth: 'auto' }}
                    >
                      Upload
                    </UploadButton>
                  </span>
                </Tooltip>
              )}

              {!isUploading && !isUploaded && (
                <Tooltip title="Remove file">
                  <IconButton
                    size="small"
                    onClick={() => handleRemove(file)}
                    disabled={state === 'UPLOADING'}
                    sx={{ color: 'text.secondary' }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              {isUploading && (
                <Tooltip title="Cancel upload">
                  <IconButton size="small" onClick={handleCancel} sx={{ color: 'error.main' }}>
                    <CancelIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>

          {/* Inline reference selection for BED files */}
          {isBedFile && (
            <Box sx={{ mt: 1.5, mb: 1.5, pl: 7 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                Reference Genome:
              </Typography>
              <RadioGroup
                row
                aria-label="reference"
                name={`reference-${file.name}`}
                value={bedReference || ''}
                onChange={(e) => setBedReferences((refs) => ({ ...refs, [file.name]: e.target.value }))}
                sx={{ ml: -1 }}
              >
                <FormControlLabel
                  value="GRCh37"
                  control={<Radio size="small" />}
                  label={<Typography variant="body2">GRCh37</Typography>}
                />
                <FormControlLabel
                  value="GRCh38"
                  control={<Radio size="small" />}
                  label={<Typography variant="body2">GRCh38</Typography>}
                />
              </RadioGroup>
              {bedChemistryOn && (
                <BedChemistryField
                  name={`chemistry-${file.name}`}
                  value={bedChemistries[file.name]}
                  onChange={(chemistry) => setBedChemistries((prev) => ({ ...prev, [file.name]: chemistry }))}
                  disabled={state === 'UPLOADING'}
                  sx={{ mt: 1 }}
                />
              )}
              <Dialog open={bedDialogOpenFor === file.name} onClose={handleCloseBedDialog}>
                <DialogTitle>Upload Bed File</DialogTitle>
                <Box p={0.5} />
                <DialogContent>
                  <DialogContentText>
                    You selected {bedReferences[file.name]}. Are you sure this is the correct reference?
                  </DialogContentText>
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleCloseBedDialog}>Cancel</Button>
                  <Button
                    onClick={() => {
                      handleUploadSingleFile()
                      handleCloseBedDialog()
                    }}
                    autoFocus
                    color="primary"
                    variant="contained"
                  >
                    Upload
                  </Button>
                </DialogActions>
              </Dialog>
            </Box>
          )}

          {/* File details section */}
          <Collapse in={!isUploading} sx={{ mt: 1 }}>
            <FileDetailGrid>
              <DetailItem>
                <Typography variant="caption" color="text.secondary">
                  Modified
                </Typography>
                <Typography variant="body2">{dateModified}</Typography>
              </DetailItem>
              <DetailItem>
                <Typography variant="caption" color="text.secondary">
                  Size
                </Typography>
                <Typography variant="body2">{formatFileSize(file.size)}</Typography>
              </DetailItem>
              <DetailItem>
                <Typography variant="caption" color="text.secondary">
                  Status
                </Typography>
                <Typography
                  variant="body2"
                  color={`${fileStatus.color}.main`}
                  sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}
                >
                  <Box
                    sx={{
                      ...fileStatus.style,
                      '&:hover': {
                        textDecoration: fileStatus.icon ? 'underline' : 'none',
                        opacity: fileStatus.icon ? 0.8 : 1,
                      },
                    }}
                    onClick={fileStatus.onClick}
                  >
                    {fileStatus.icon}
                    {fileStatus.label}
                  </Box>
                </Typography>
              </DetailItem>
            </FileDetailGrid>
          </Collapse>

          {/* Progress bar for uploading files */}
          {isUploading && (
            <Box sx={{ mt: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {`${uploadProgress.percentCompleted}%`}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
                  <TimerIcon fontSize="inherit" sx={{ mr: 0.5 }} />
                  {formatTimeRemaining(uploadProgress.timeRemaining)}
                </Typography>
              </Box>
              <ProgressBarStyle variant="determinate" value={parseFloat(uploadProgress.percentCompleted)} />
            </Box>
          )}
        </FileItemStyle>
      </motion.div>
    )
  }

  const hasAnyFiles = () => {
    return (
      fastqQueue.size > 0 ||
      vcfQueue.length > 0 ||
      bamQueue.length > 0 ||
      bedQueue.length > 0 ||
      genePanelQueue.length > 0
    )
  }

  const getTotalFiles = () => {
    return getFilesToUpload().length
  }

  const getFastqFiles = () => {
    const fastqFiles = []
    fastqQueue.forEach((entry) => {
      if (entry.left) fastqFiles.push(entry.left)
      if (entry.right) fastqFiles.push(entry.right)
    })
    return fastqFiles
  }

  // Stable list for the naming check: getFastqFiles() builds a fresh array every
  // render, which would re-run the check's memo each time.
  const fastqFileNames = useMemo(() => {
    const names = []
    fastqQueue.forEach((entry) => {
      if (entry.left) names.push(entry.left.name)
      if (entry.right) names.push(entry.right.name)
    })
    return names
  }, [fastqQueue])

  // Helper to trigger a click on the Upload Tracker icon
  const openUploadTracker = () => {
    // Find the upload tracker icon and click it
    const uploadTrackerIcon =
      document.querySelector('[aria-label="Upload Tracker"]') || document.querySelector('.MuiBadge-root')
    if (uploadTrackerIcon) {
      // Find the closest button or clickable element
      const button = uploadTrackerIcon.closest('button') || uploadTrackerIcon
      button.click()
    }
  }

  const getFileStatus = (file) => {
    if (uploadedFiles.includes(file)) {
      return {
        label: 'Track in Upload Tracker',
        color: 'info',
        icon: <CloudUploadIcon fontSize="small" />,
        style: {
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '4px',
          fontWeight: 'medium',
          padding: '2px 6px',
          borderRadius: '4px',
        },
        onClick: openUploadTracker,
      }
    }

    if (state === 'UPLOADING' && filesToUpload[0] === file) {
      return {
        label: 'Processing in Upload Tracker',
        color: 'primary',
        icon: <TimerIcon fontSize="small" />,
        style: {
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '4px',
          fontWeight: 'medium',
          padding: '2px 6px',
          borderRadius: '4px',
        },
        onClick: openUploadTracker,
      }
    }

    if (state === 'READY_TO_UPLOAD') {
      return { label: 'Ready', color: 'info' }
    }

    return { label: 'Queued', color: 'default' }
  }

  // Without this a file over the size cap is dropped with no feedback at all.
  const handleDropRejected = (rejections) => {
    const tooLarge = rejections.filter((r) => r.errors.some((e) => e.code === 'file-too-large'))
    if (tooLarge.length > 0) {
      enqueueSnackbar(
        `File too large (max ${formatFileSize(MAX_UPLOAD_SIZE_BYTES)}): ${tooLarge
          .map((r) => `${r.file.name} (${formatFileSize(r.file.size)})`)
          .join(', ')}`,
        { variant: 'error' },
      )
    }
  }

  // Move useDropzone call after handleDropFile is defined
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDropFile,
    onDropRejected: handleDropRejected,
    maxSize: MAX_UPLOAD_SIZE_BYTES,
    noClick: state === 'UPLOADING',
  })

  // Add a new function to render paired FASTQ files
  const renderFastqPair = (fileId, pair, index) => {
    const { left, right } = pair
    const isUploading =
      state === 'UPLOADING' &&
      ((left && !uploadedFiles.includes(left) && filesToUpload[0] === left) ||
        (right && !uploadedFiles.includes(right) && filesToUpload[0] === right))

    const isUploaded = (left && uploadedFiles.includes(left)) || (right && uploadedFiles.includes(right))
    const fileStatus = isUploaded
      ? {
          label: 'Track in Upload Tracker',
          color: 'info',
          icon: <CloudUploadIcon fontSize="small" />,
          style: {
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px',
            fontWeight: 'medium',
            padding: '2px 6px',
            borderRadius: '4px',
          },
          onClick: openUploadTracker,
        }
      : state === 'READY_TO_UPLOAD'
      ? { label: 'Ready', color: 'info' }
      : { label: 'Queued', color: 'default' }

    // Calculate total size of the pair
    const totalSize = (left ? left.size : 0) + (right ? right.size : 0)

    // Get most recent modification date
    const leftModified = left ? new Date(left.lastModified) : new Date(0)
    const rightModified = right ? new Date(right.lastModified) : new Date(0)
    const mostRecentDate = new Date(Math.max(leftModified, rightModified))
    const dateModified = mostRecentDate.toLocaleString()

    // Handle upload of the pair
    const handleUploadPair = async () => {
      try {
        if (left) {
          await uploadFile(left)
          setUploadedFiles((prev) => [...prev, left])
        }
        if (right) {
          await uploadFile(right)
          setUploadedFiles((prev) => [...prev, right])
        }
        filesApi.refresh()
      } catch (error) {
        enqueueSnackbar(`Failed to upload files`, { variant: 'error' })
      }
    }

    // Handle removing the pair
    const handleRemovePair = () => {
      if (left) handleRemove(left)
      if (right) handleRemove(right)
    }

    return (
      <motion.div
        key={`pair-${fileId}-${index}`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, x: -10 }}
        transition={{ duration: 0.2, delay: index * 0.05 }}
      >
        <FileItemStyle>
          {/* Main row with file info and actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <Box
                sx={{
                  mr: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 40,
                  height: 40,
                  borderRadius: 1,
                  bgcolor: (theme) => alpha(theme.palette.background.default, 0.7),
                }}
              >
                <DescriptionIcon sx={{ color: 'primary.main' }} />
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <Typography
                    variant="subtitle2"
                    noWrap
                    title={fileId}
                    sx={{ maxWidth: { xs: 150, sm: 250, md: 350 } }}
                  >
                    {fileId}
                  </Typography>
                  <FileTypeChip label="PAIRED FASTQ" size="small" color="primary" sx={{ ml: 1 }} />
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  component="div"
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  {formatFileSize(totalSize)}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {!isUploaded && !isUploading && (
                <Tooltip title="Upload this pair">
                  <UploadButton
                    variant="contained"
                    size="small"
                    startIcon={<CloudUploadIcon fontSize="small" />}
                    onClick={handleUploadPair}
                    disabled={state === 'UPLOADING'}
                    color="primary"
                    sx={{ minWidth: 'auto' }}
                  >
                    Upload
                  </UploadButton>
                </Tooltip>
              )}

              {!isUploading && !isUploaded && (
                <Tooltip title="Remove pair">
                  <IconButton
                    size="small"
                    onClick={handleRemovePair}
                    disabled={state === 'UPLOADING'}
                    sx={{ color: 'text.secondary' }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              {isUploading && (
                <Tooltip title="Cancel upload">
                  <IconButton size="small" onClick={handleCancel} sx={{ color: 'error.main' }}>
                    <CancelIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>

          {/* Paired files container */}
          <PairedFileContainer>
            {/* Left (R1) file */}
            {left && (
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 1,
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      mr: 1,
                    }}
                  >
                    R1
                  </Box>
                  <Typography variant="body2" noWrap title={left.name}>
                    {shortenFileName(left.name)}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ pl: 4 }}>
                  {formatFileSize(left.size)}
                </Typography>
              </Box>
            )}

            {/* Right (R2) file */}
            {right && (
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 1,
                      bgcolor: 'secondary.main',
                      color: 'secondary.contrastText',
                      mr: 1,
                    }}
                  >
                    R2
                  </Box>
                  <Typography variant="body2" noWrap title={right.name}>
                    {shortenFileName(right.name)}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ pl: 4 }}>
                  {formatFileSize(right.size)}
                </Typography>
              </Box>
            )}
          </PairedFileContainer>

          {/* File details section */}
          <Collapse in={!isUploading} sx={{ mt: 1 }}>
            <FileDetailGrid>
              <DetailItem>
                <Typography variant="caption" color="text.secondary">
                  Modified
                </Typography>
                <Typography variant="body2">{dateModified}</Typography>
              </DetailItem>
              <DetailItem>
                <Typography variant="caption" color="text.secondary">
                  Size
                </Typography>
                <Typography variant="body2">{formatFileSize(totalSize)}</Typography>
              </DetailItem>
              <DetailItem>
                <Typography variant="caption" color="text.secondary">
                  Status
                </Typography>
                <Typography
                  variant="body2"
                  color={`${fileStatus.color}.main`}
                  sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}
                >
                  <Box
                    sx={{
                      ...fileStatus.style,
                      '&:hover': {
                        textDecoration: fileStatus.icon ? 'underline' : 'none',
                        opacity: fileStatus.icon ? 0.8 : 1,
                      },
                    }}
                    onClick={fileStatus.onClick}
                  >
                    {fileStatus.icon}
                    {fileStatus.label}
                  </Box>
                </Typography>
              </DetailItem>
            </FileDetailGrid>
          </Collapse>

          {/* Progress bar for uploading files */}
          {isUploading && (
            <Box sx={{ mt: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {`${uploadProgress.percentCompleted}%`}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
                  <TimerIcon fontSize="inherit" sx={{ mr: 0.5 }} />
                  {formatTimeRemaining(uploadProgress.timeRemaining)}
                </Typography>
              </Box>
              <ProgressBarStyle variant="determinate" value={parseFloat(uploadProgress.percentCompleted)} />
            </Box>
          )}
        </FileItemStyle>
      </motion.div>
    )
  }

  const isAnyBedReferenceUndefined = bedQueue.some((file) => bedReferences[file.name] === undefined)

  return (
    <StyledCard>
      <StyledCardContent>
        {!hasAnyFiles() && (
          <UploadHeader>
            <Box>
              <UploadTitle>Upload Files</UploadTitle>
              <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                Upload FASTQ, VCF, BED, and Gene Panels
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {state === 'UPLOADING' && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<CancelIcon />}
                  onClick={handleCancel}
                  sx={{ borderRadius: 8 }}
                >
                  Cancel Upload
                </Button>
              )}
            </Box>
          </UploadHeader>
        )}

        {hasAnyFiles() && (
          <UploadHeader>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <UploadTitle>Selected Files</UploadTitle>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Naming rules, alongside the other actions rather than inside a
                  banner: the examples are what people need before uploading. */}
              <FastqNamingInfo variant="button" />

              <BrowseFilesButton
                variant="outlined"
                component="label"
                size="small"
                startIcon={<CloudUploadIcon />}
                disabled={state === 'UPLOADING'}
                sx={{
                  fontSize: '0.75rem',
                  py: 0.75,
                  px: 2,
                  borderRadius: 20,
                  minWidth: '110px',
                  height: '32px',
                }}
              >
                Add More
                <input {...getInputProps()} style={{ display: 'none' }} disabled={state === 'UPLOADING'} />
              </BrowseFilesButton>

              {hasAnyFiles() && state === 'READY_TO_UPLOAD' && (
                <Tooltip
                  title={isAnyBedReferenceUndefined ? 'Select reference genome for BED files' : 'Upload all files'}
                >
                  <span>
                    <UploadButton
                      variant="contained"
                      size="small"
                      startIcon={<CloudUploadIcon />}
                      onClick={() => {
                        if (bedQueue.length > 0) {
                          setBedDialogOpenForAll(true)
                        } else {
                          handleUploadFiles()
                        }
                      }}
                      disabled={state === 'UPLOADING' || isAnyBedReferenceUndefined}
                      sx={{
                        borderRadius: 20,
                        fontSize: '0.75rem',
                        py: 0.75,
                        px: 2,
                        minWidth: '110px',
                        height: '32px',
                      }}
                    >
                      Upload All
                    </UploadButton>

                    <Dialog open={bedDialogOpenForAll} onClose={handleCloseBedDialogForAll}>
                      <DialogTitle>Upload All Files</DialogTitle>
                      <Box p={0.5} />
                      <DialogContent>
                        <DialogContentText>
                          You selected reference genomes for all BED files. Are you sure these are the correct reference
                          genomes?
                        </DialogContentText>
                      </DialogContent>
                      <DialogActions>
                        <Button onClick={handleCloseBedDialogForAll}>Cancel</Button>
                        <Button
                          onClick={() => {
                            handleUploadFiles()
                            handleCloseBedDialogForAll()
                          }}
                          autoFocus
                          color="primary"
                          variant="contained"
                        >
                          Upload All
                        </Button>
                      </DialogActions>
                    </Dialog>
                  </span>
                </Tooltip>
              )}

              {hasAnyFiles() && (
                <Tooltip title="Clear all files">
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleRemoveAll}
                    disabled={state === 'UPLOADING'}
                    sx={{
                      borderRadius: 20,
                      fontSize: '0.75rem',
                      py: 0.75,
                      px: 2,
                      borderColor: 'error.main',
                      color: 'error.main',
                      minWidth: '110px',
                      height: '32px',
                      '&:hover': {
                        borderColor: 'error.dark',
                        backgroundColor: 'error.main',
                        color: 'error.contrastText',
                      },
                    }}
                  >
                    Clear All
                  </Button>
                </Tooltip>
              )}

              {state === 'UPLOADING' && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<CancelIcon />}
                  onClick={handleCancel}
                  sx={{ borderRadius: 8 }}
                >
                  Cancel Upload
                </Button>
              )}
            </Box>
          </UploadHeader>
        )}

        {/* Show file list when files are added, otherwise show empty state */}
        {hasAnyFiles() ? (
          <FileListContainer>
            {/* Naming check: runs the queued FASTQ names through the same rules the
                server pairs by, so an unpairable or mislabelling name is caught here
                rather than after tens of gigabytes have been uploaded. */}
            <FastqNamingCheck fileNames={fastqFileNames} />

            {/* File type tabs */}
            <StyledTabs value={activeTab} onChange={handleTabChange} aria-label="upload file types">
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="subtitle2">All Files</Typography>
                    {getTotalFiles() > 0 && (
                      <FileCountBadge sx={{ ml: 1, py: 0.25, px: 0.5 }}>{getTotalFiles()}</FileCountBadge>
                    )}
                  </Box>
                }
                {...a11yProps(0)}
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="subtitle2">FASTQ</Typography>
                    {getFastqFiles().length > 0 && (
                      <FileCountBadge sx={{ ml: 1, py: 0.25, px: 0.5 }}>{getFastqFiles().length}</FileCountBadge>
                    )}
                  </Box>
                }
                {...a11yProps(1)}
                disabled={getFastqFiles().length === 0}
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="subtitle2">VCF</Typography>
                    {vcfQueue.length > 0 && (
                      <FileCountBadge sx={{ ml: 1, py: 0.25, px: 0.5 }}>{vcfQueue.length}</FileCountBadge>
                    )}
                  </Box>
                }
                {...a11yProps(2)}
                disabled={vcfQueue.length === 0}
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="subtitle2">BAM</Typography>
                    {bamQueue.length > 0 && (
                      <FileCountBadge sx={{ ml: 1, py: 0.25, px: 0.5 }}>{bamQueue.length}</FileCountBadge>
                    )}
                  </Box>
                }
                {...a11yProps(3)}
                disabled={bamQueue.length === 0}
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="subtitle2">BED</Typography>
                    {bedQueue.length > 0 && (
                      <FileCountBadge sx={{ ml: 1, py: 0.25, px: 0.5 }}>{bedQueue.length}</FileCountBadge>
                    )}
                  </Box>
                }
                {...a11yProps(4)}
                disabled={bedQueue.length === 0}
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="subtitle2">Gene Panels</Typography>
                    {genePanelQueue.length > 0 && (
                      <FileCountBadge sx={{ ml: 1, py: 0.25, px: 0.5 }}>{genePanelQueue.length}</FileCountBadge>
                    )}
                  </Box>
                }
                {...a11yProps(5)}
                disabled={genePanelQueue.length === 0}
              />
            </StyledTabs>

            {/* Scrollable file list */}
            <TabPanel value={activeTab} index={0}>
              <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <ScrollableFileContainer>
                  {/* Render FASTQ files as pairs */}
                  {[...fastqQueue.entries()].map(([fileId, pair], index) => renderFastqPair(fileId, pair, index))}
                  {vcfQueue.map((file, index) => renderFileItem(file, index, FILE_TYPES.VCF))}
                  {bamQueue.map((file, index) => renderFileItem(file, index, FILE_TYPES.BAM))}
                  {bedQueue.map((file, index) => renderFileItem(file, index, FILE_TYPES.BED))}
                  {genePanelQueue.map((file, index) => renderFileItem(file, index, FILE_TYPES.GENE_PANEL))}
                </ScrollableFileContainer>
              </Box>
            </TabPanel>

            <TabPanel value={activeTab} index={1}>
              <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <ScrollableFileContainer>
                  {/* Render FASTQ files as pairs */}
                  {[...fastqQueue.entries()].map(([fileId, pair], index) => renderFastqPair(fileId, pair, index))}
                </ScrollableFileContainer>
              </Box>
            </TabPanel>

            <TabPanel value={activeTab} index={2}>
              <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <ScrollableFileContainer>
                  {vcfQueue.map((file, index) => renderFileItem(file, index, FILE_TYPES.VCF))}
                </ScrollableFileContainer>
              </Box>
            </TabPanel>

            <TabPanel value={activeTab} index={3}>
              <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <ScrollableFileContainer>
                  {bamQueue.map((file, index) => renderFileItem(file, index, FILE_TYPES.BAM))}
                </ScrollableFileContainer>
              </Box>
            </TabPanel>

            <TabPanel value={activeTab} index={4}>
              <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <ScrollableFileContainer>
                  {bedQueue.map((file, index) => renderFileItem(file, index, FILE_TYPES.BED))}
                </ScrollableFileContainer>
              </Box>
            </TabPanel>

            <TabPanel value={activeTab} index={5}>
              <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <ScrollableFileContainer>
                  {genePanelQueue.map((file, index) => renderFileItem(file, index, FILE_TYPES.GENE_PANEL))}
                </ScrollableFileContainer>
              </Box>
            </TabPanel>
          </FileListContainer>
        ) : (
          /* Upload area when no files are selected */
          <DropZoneStyle {...getRootProps()} isDragActive={isDragActive}>
            <input {...getInputProps()} disabled={state === 'UPLOADING'} />

            <CloudUploadIcon
              sx={{
                fontSize: 64,
                color: 'primary.main',
                mb: 3,
                opacity: 0.9,
              }}
            />

            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
              {isDragActive ? 'Drop files here' : 'Drag & drop files here'}
            </Typography>

            <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', mb: 3 }}>
              or
              <Button
                variant="outlined"
                component="span"
                sx={{
                  mx: 1,
                  border: '2px dashed',
                  borderColor: (theme) => alpha(theme.palette.primary.main, 0.5),
                  color: 'primary.main',
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  backgroundColor: 'transparent',
                  '&:hover': {
                    border: '2px dashed',
                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
                    borderColor: 'primary.main',
                    color: 'primary.main',
                  },
                }}
                disabled={state === 'UPLOADING'}
              >
                Browse Files
              </Button>
              from your computer
            </Typography>

            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                justifyContent: 'center',
                maxWidth: 500,
              }}
            >
              {[
                { label: 'FASTQ', exts: ['.fastq', '.fq', '.fastq.gz', '.fq.gz'] },
                { label: 'VCF', exts: ['.vcf', '.vcf.gz'] },
                { label: 'BAM', exts: ['.bam'] },
                { label: 'BED', exts: ['.bed'] },
                { label: 'Gene Panels', exts: ['.txt', '.csv'] },
              ].map((item) => (
                <Chip
                  key={item.label}
                  label={item.label}
                  size="small"
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    backgroundColor: 'background.paper',
                    borderColor: alpha(theme.palette.divider, 0.3),
                    color: 'text.primary',
                    '& .MuiChip-label': {
                      px: 1.5,
                      py: 0.5,
                    },
                  }}
                />
              ))}
            </Box>

            {/* Naming rules, reachable before anything is selected: a wrong read
                indicator is only discoverable after the upload otherwise. */}
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                How should FASTQ files be named?
              </Typography>
              <FastqNamingInfo />
            </Box>
          </DropZoneStyle>
        )}

        <Box sx={{ mt: 4, display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
          <InfoIcon sx={{ fontSize: 16, mr: 1 }} />
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
            Supported formats:
            <ClickAwayTooltip
              title={
                <div>
                  <div style={{ textAlign: 'justify' }}>
                    The FASTQ file format is a widely adopted standard in bioinformatics for the storage of
                    high-throughput sequencing data, comprising a four-line structure for each sequence read that
                    includes a unique identifier, the nucleotide sequence, a separator line, and a corresponding line of
                    quality scores encoded in ASCII, thereby facilitating the assessment of sequencing accuracy and
                    error rates.
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      {['.fastq', '.fq', '.fastq.gz', '.fq.gz'].map((ext) => (
                        <span
                          key={ext}
                          style={{
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            padding: '2px 6px',
                            fontSize: '0.9em',
                          }}
                        >
                          {ext}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              }
              arrow
              placement="top"
            >
              <FileTypeLink>FASTQ</FileTypeLink>
            </ClickAwayTooltip>
            ,
            <ClickAwayTooltip
              title={
                <div>
                  <div style={{ textAlign: 'justify' }}>
                    The Variant Call Format (VCF) is a standardized text file format utilized in bioinformatics for the
                    representation and storage of genetic variation data, including single nucleotide polymorphisms
                    (SNPs), insertions, deletions, and structural variants, structured with a header that provides
                    metadata and a series of records detailing the genomic positions and attributes of each variant
                    identified during DNA sequencing analyses.
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      {['.vcf', '.vcf.gz'].map((ext) => (
                        <span
                          key={ext}
                          style={{
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            padding: '2px 6px',
                            fontSize: '0.9em',
                          }}
                        >
                          {ext}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              }
              arrow
              placement="top"
            >
              <FileTypeLink>VCF</FileTypeLink>
            </ClickAwayTooltip>
            ,
            <ClickAwayTooltip
              title={
                <div>
                  <div style={{ textAlign: 'justify' }}>
                    The BAM (Binary Alignment Map) file format is the compressed binary representation of SAM (Sequence
                    Alignment Map) files, used to store aligned sequencing reads against a reference genome. BAM files
                    from Oxford Nanopore Technology (ONT) long-read sequencers are processed through the
                    wf-human-variation pipeline for variant calling.
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      <span
                        style={{
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          padding: '2px 6px',
                          fontSize: '0.9em',
                        }}
                      >
                        .bam
                      </span>
                    </div>
                  </div>
                </div>
              }
              arrow
              placement="top"
            >
              <FileTypeLink>BAM</FileTypeLink>
            </ClickAwayTooltip>
            ,
            <ClickAwayTooltip
              title={
                <div>
                  <div style={{ textAlign: 'justify' }}>
                    The BED (Browser Extensible Data) format is a text file format used to store genomic regions as
                    coordinates and associated annotations. The data are presented in the form of columns separated by
                    spaces or tabs.
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      <span
                        style={{
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          padding: '2px 6px',
                          fontSize: '0.9em',
                        }}
                      >
                        .bed
                      </span>
                    </div>
                  </div>
                </div>
              }
              arrow
              placement="top"
            >
              <FileTypeLink>Capture Kits</FileTypeLink>
            </ClickAwayTooltip>
            , and
            <ClickAwayTooltip
              title={
                <div>
                  <div style={{ textAlign: 'justify' }}>
                    List of gene names. Should be comma separated or xlsx file. The file name will be used as the gene
                    panel name.
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      {['.txt', '.csv'].map((ext) => (
                        <span
                          key={ext}
                          style={{
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            padding: '2px 6px',
                            fontSize: '0.9em',
                          }}
                        >
                          {ext}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              }
              arrow
              placement="top"
            >
              <FileTypeLink>Gene Panels</FileTypeLink>
            </ClickAwayTooltip>
          </Typography>
        </Box>

        {/* Result files from a tumour test done elsewhere have their own upload,
            so a gene list is never mistaken for one. */}
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Results from a tumour test done elsewhere?
          </Typography>
          <Button
            size="small"
            onClick={() => setResultUpload({ open: true, files: [] })}
            disabled={state === 'UPLOADING'}
            sx={{ textTransform: 'none', fontWeight: 600, py: 0 }}
            data-testid="open-result-upload"
          >
            Upload tumour test results
          </Button>
        </Box>
      </StyledCardContent>

      <ResultFileDialog
        open={resultUpload.open}
        files={resultUpload.files}
        onClose={() => setResultUpload({ open: false, files: [] })}
        onStartAnalysis={(vcfFileIds) => setResultStart({ open: true, files: vcfFileIds })}
      />

      {resultStart.open && (
        <NewAnalysisDialog
          open
          initialFiles={resultStart.files}
          initialWorkflow={SOMATIC_VCF_WORKFLOW}
          onClose={() => setResultStart({ open: false, files: [] })}
        />
      )}

      {/* Gene Validation Dialog */}
      <StyledDialog
        open={isGeneValidationDialogOpen}
        onClose={() => {
          setIsGeneValidationDialogOpen(false)
          setGenePanelQueue([])
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main', display: 'flex', alignItems: 'center', gap: 1 }}>
          <CancelIcon /> Invalid Genes Detected
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>The following genes are not valid:</Typography>
          <Box
            sx={{
              p: 2,
              backgroundColor: (theme) => alpha(theme.palette.error.light, 0.1),
              borderRadius: 1,
              mb: 2,
            }}
          >
            <Typography color="error">
              {invalidGenes.slice(0, 10).join(', ')}
              {invalidGenes.length > 10 ? ` (and ${invalidGenes.length - 10} more)` : ''}
            </Typography>
          </Box>
          <Typography>Please remove these genes and try uploading again.</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              setIsGeneValidationDialogOpen(false)
              setGenePanelQueue([])
            }}
          >
            Close
          </Button>
        </DialogActions>
      </StyledDialog>

      {/* Notes Dialog */}
      <StyledDialog
        open={isNotesDialogOpen}
        onClose={() => {
          setIsNotesDialogOpen(false)
          setState('READY_TO_UPLOAD')
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Notes</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="notes"
            label="Notes"
            type="text"
            fullWidth
            multiline
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setIsNotesDialogOpen(false)
              setGenePanelQueue([])
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setIsNotesDialogOpen(false)
              setState('READY_TO_UPLOAD')
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </StyledDialog>

      {/* BAM Platform Selector Dialog */}
      <Dialog open={bamPlatformDialogOpen} onClose={handleBamPlatformCancel} maxWidth="xs" fullWidth>
        <DialogTitle>Select BAM Platform</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Select the sequencing platform for the BAM file{pendingBamFiles.length > 1 ? 's' : ''}:
          </Typography>
          <RadioGroup value={selectedBamPlatform} onChange={(e) => setSelectedBamPlatform(e.target.value)}>
            <FormControlLabel value="ont_bam" control={<Radio />} label="Oxford Nanopore (ONT)" />
            <FormControlLabel value="pacbio_bam" control={<Radio />} label="PacBio HiFi" />
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBamPlatformCancel}>Cancel</Button>
          <Button variant="contained" onClick={handleBamPlatformConfirm}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* VCF Variant Type Selector Dialog — one choice per pending file */}
      <Dialog open={vcfVariantTypeDialogOpen} onClose={handleVcfVariantTypeCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Select VCF Variant Type</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Pick the kind of variants in each VCF file. This determines the analysis pipeline (SNP/Indel runs the
            standard annotation pipeline; CNV and SV are annotated with AnnotSV).
          </Typography>
          <Stack spacing={1.5}>
            {pendingVcfFiles.map((file) => (
              <Box
                key={file.name}
                sx={{
                  p: 1.25,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                <Typography variant="body2" noWrap title={file.name} sx={{ fontWeight: 600, mb: 0.5 }}>
                  {file.name}
                </Typography>
                <RadioGroup
                  row
                  value={pendingVcfTypes[file.name] || 'snp_indel'}
                  onChange={(e) => handlePendingVcfTypeChange(file.name, e.target.value)}
                >
                  <FormControlLabel value="snp_indel" control={<Radio size="small" />} label="SNP / Indel" />
                  <FormControlLabel value="cnv" control={<Radio size="small" />} label="CNV" />
                  <FormControlLabel value="sv" control={<Radio size="small" />} label="SV" />
                </RadioGroup>
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleVcfVariantTypeCancel}>Cancel</Button>
          <Button variant="contained" onClick={handleVcfVariantTypeConfirm}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </StyledCard>
  )
}

export default UnifiedFileUpload
